import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { transactions } from '../../services/api';
import { Transaction, TransactionFilters } from '../../types';
import { useTransactions } from '../../contexts/TransactionContext';
import EditTransactionDialog from './EditTransactionDialog';
import NewTransactionDialog from './NewTransactionDialog';

interface LocalFilters {
  startDate: string;
  endDate: string;
  description: string;
  category: string;
  type: string;
  minAmount: string;
  maxAmount: string;
}

const TransactionList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { 
    transactions: transactionList,
    loading,
    pagination, 
    loadingMore,
    pageSize,
    setPageSize,
    loadPage,
    loadMoreTransactions,
    getAllTransactions,
    setFilters: setContextFilters,
    filters
  } = useTransactions();
  
  const [error, setError] = useState<string | null>(null);
  const [isNewTransactionDialogOpen, setIsNewTransactionDialogOpen] = useState(false);
  const [isEditTransactionDialogOpen, setIsEditTransactionDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null);
  
  // Local filter state for the form
  const [localFilters, setLocalFilters] = useState<LocalFilters>({
    startDate: '',
    endDate: '',
    description: '',
    category: '',
    type: '',
    minAmount: '',
    maxAmount: ''
  });
  
  // State for client-side filtering when needed
  const [showFiltered, setShowFiltered] = useState(false);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);

  // Load transactions when component mounts (skip budget filtering for transactions page)
  useEffect(() => {
    loadPage(1, true); // Skip budget filter to load all transactions
  }, []); // Empty dependency array - only run on mount

  // Apply filters function
  const applyFilters = useCallback(() => {
    const filters: TransactionFilters = {};
    
    // Convert date strings to ISO format for the API
    if (localFilters.startDate) {
      const startDate = new Date(localFilters.startDate);
      startDate.setUTCHours(0, 0, 0, 0);
      filters.startDate = startDate.toISOString();
    }
    if (localFilters.endDate) {
      const endDate = new Date(localFilters.endDate);
      endDate.setUTCHours(23, 59, 59, 999);
      filters.endDate = endDate.toISOString();
    }
    if (localFilters.description) filters.description = localFilters.description;
    if (localFilters.category) filters.category = localFilters.category;
    if (localFilters.type) filters.type = localFilters.type;
    if (localFilters.minAmount) filters.minAmount = localFilters.minAmount;
    if (localFilters.maxAmount) filters.maxAmount = localFilters.maxAmount;
    
    setContextFilters(filters);
    setShowFiltered(false);
    // Filtering happens instantly via computed properties in TransactionContext
  }, [localFilters, setContextFilters]);

  // Clear filters function
  const handleClearFilters = useCallback(() => {
    setLocalFilters({
      startDate: '',
      endDate: '',
      description: '',
      category: '',
      type: '',
      minAmount: '',
      maxAmount: ''
    });
    setContextFilters({});
    setShowFiltered(false);
    // Filtering is cleared instantly via computed properties in TransactionContext
  }, [setContextFilters]);

  // Client-side filtering for "Filter All" functionality
  const filterAllTransactions = useCallback(async () => {
    // First get all transactions
    await getAllTransactions(true); // Skip budget filter to get all transactions
    
    // The getAllTransactions function updates the allTransactions state
    // But we need to use the response directly since state updates are async
    // Let's call the API directly to get fresh data for filtering
    try {
      const response = await transactions.getAll({ getAllForFilters: true });
      let result = [...response.transactions];
      
      // Apply local filters to all transactions
      if (localFilters.startDate) {
        const startDate = new Date(localFilters.startDate);
        startDate.setUTCHours(0, 0, 0, 0);
        result = result.filter(t => new Date(t.date) >= startDate);
      }
      if (localFilters.endDate) {
        const endDate = new Date(localFilters.endDate);
        endDate.setUTCHours(23, 59, 59, 999);
        result = result.filter(t => new Date(t.date) <= endDate);
      }
      if (localFilters.description) {
        const searchTerm = localFilters.description.toLowerCase();
        result = result.filter(t => t.description.toLowerCase().includes(searchTerm));
      }
      if (localFilters.category) {
        const searchTerm = localFilters.category.toLowerCase();
        result = result.filter(t => t.category.toLowerCase().includes(searchTerm));
      }
      if (localFilters.type) {
        result = result.filter(t => t.type === localFilters.type);
      }
      if (localFilters.minAmount) {
        result = result.filter(t => t.amount >= parseFloat(localFilters.minAmount));
      }
      if (localFilters.maxAmount) {
        result = result.filter(t => t.amount <= parseFloat(localFilters.maxAmount));
      }

      setFilteredTransactions(result);
      setShowFiltered(true);
    } catch (error) {
      console.error('Error filtering all transactions:', error);
    }
  }, [localFilters, getAllTransactions]);

  const handleCreateTransaction = async (newTransaction: Omit<Transaction, '_id' | 'user' | 'createdAt'>) => {
    try {
      await transactions.create(newTransaction);
      setIsNewTransactionDialogOpen(false);
      await loadPage(1, true); // Refresh first page without budget filter
    } catch (err) {
      setError('Failed to create transaction. Please try again.');
      console.error('Error creating transaction:', err);
    }
  };

  const handleEditTransaction = async (transactionId: string, updatedTransaction: Partial<Transaction>) => {
    try {
      await transactions.update(transactionId, updatedTransaction);
      setIsEditTransactionDialogOpen(false);
      setSelectedTransaction(null);
      await loadPage(pagination?.currentPage || 1, true); // Refresh current page without budget filter
    } catch (err) {
      setError('Failed to update transaction. Please try again.');
      console.error('Error updating transaction:', err);
    }
  };

  const handleDeleteClick = (transactionId: string) => {
    setTransactionToDelete(transactionId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;
    
    try {
      await transactions.delete(transactionToDelete);
      setIsDeleteDialogOpen(false);
      setTransactionToDelete(null);
      await loadPage(pagination?.currentPage || 1, true); // Refresh current page without budget filter
    } catch (err) {
      setError('Failed to delete transaction. Please try again.');
      console.error('Error deleting transaction:', err);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setTransactionToDelete(null);
  };

  const handleEditClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsEditTransactionDialogOpen(true);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    loadPage(page, true); // Load page without budget filter
  };

  const handlePageSizeChange = (event: any) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get current transactions to display
  const currentTransactions = showFiltered ? filteredTransactions : transactionList;

  if (loading && !loadingMore) {
    return (
      <Container 
        maxWidth={isMobile ? "sm" : "xl"} 
        sx={{ 
          py: { xs: 1, sm: 2 }, 
          px: { xs: 1, sm: 2, md: 3 },
          position: 'relative' 
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={isMobile ? "sm" : "xl"} 
      sx={{ 
        py: { xs: 1, sm: 2 }, 
        px: { xs: 1, sm: 2, md: 3 },
        position: 'relative' 
      }}
    >
      <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Transactions
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsNewTransactionDialogOpen(true)}
          sx={{
            backgroundColor: '#7C8B6F',
            '&:hover': {
              backgroundColor: '#6A7860'
            }
          }}
        >
          New Transaction
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <TextField
                label="Start Date"
                type="date"
                value={localFilters.startDate}
                onChange={(e) => setLocalFilters({ ...localFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End Date"
                type="date"
                value={localFilters.endDate}
                onChange={(e) => setLocalFilters({ ...localFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Description"
                value={localFilters.description}
                onChange={(e) => setLocalFilters({ ...localFilters, description: e.target.value })}
                size="small"
                InputProps={{
                  endAdornment: localFilters.description && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setLocalFilters({ ...localFilters, description: '' })}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Category"
                value={localFilters.category}
                onChange={(e) => setLocalFilters({ ...localFilters, category: e.target.value })}
                size="small"
                InputProps={{
                  endAdornment: localFilters.category && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setLocalFilters({ ...localFilters, category: '' })}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                select
                label="Type"
                value={localFilters.type}
                onChange={(e) => setLocalFilters({ ...localFilters, type: e.target.value })}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="income">Income</MenuItem>
                <MenuItem value="expense">Expense</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
              </TextField>
              <TextField
                label="Min Amount"
                type="number"
                value={localFilters.minAmount}
                onChange={(e) => setLocalFilters({ ...localFilters, minAmount: e.target.value })}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              <TextField
                label="Max Amount"
                type="number"
                value={localFilters.maxAmount}
                onChange={(e) => setLocalFilters({ ...localFilters, maxAmount: e.target.value })}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Box>
            <Box display="flex" gap={2}>
              <Button variant="contained" onClick={applyFilters} size="small">
                Apply Filters (Paginated)
              </Button>
              <Button variant="outlined" onClick={filterAllTransactions} size="small">
                Filter All Transactions
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} size="small">
                Clear Filters
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small">
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label="Page Size"
              sx={{ minWidth: 100 }}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={250}>250</MenuItem>
            </Select>
          </FormControl>
          {showFiltered && (
            <Typography variant="body2" color="primary">
              Showing {filteredTransactions.length} filtered results
            </Typography>
          )}
          {!showFiltered && Object.keys(filters).length > 0 && (
            <Typography variant="body2" color="primary">
              Showing {transactionList.length} filtered results from loaded data
            </Typography>
          )}
          {!showFiltered && Object.keys(filters).length === 0 && pagination && (
            <Typography variant="body2">
              Showing {((pagination.currentPage - 1) * pagination.limit) + 1}-{Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} transactions
            </Typography>
          )}
        </Box>
        
        {!showFiltered && Object.keys(filters).length === 0 && pagination && pagination.totalPages > 1 && (
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentTransactions.map((transaction) => (
              <TableRow key={transaction._id}>
                <TableCell>{formatDate(transaction.date)}</TableCell>
                <TableCell>{transaction.description}</TableCell>
                <TableCell>{transaction.category}</TableCell>
                <TableCell>{transaction.type}</TableCell>
                <TableCell align="right">{formatCurrency(transaction.amount)}</TableCell>
                <TableCell>
                  <Tooltip title="Edit Transaction">
                    <IconButton size="small" onClick={() => handleEditClick(transaction)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Transaction">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteClick(transaction._id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {currentTransactions.length === 0 && !loading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography variant="body1" color="textSecondary">
              No transactions found
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Load More Button for paginated results */}
      {!showFiltered && Object.keys(filters).length === 0 && pagination && pagination.hasNextPage && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            onClick={() => loadMoreTransactions(true)}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

      {/* Load More Data button for when filters are active */}
      {!showFiltered && Object.keys(filters).length > 0 && pagination && pagination.hasNextPage && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            onClick={() => loadMoreTransactions()}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
          >
            {loadingMore ? 'Loading More Data...' : 'Load More Data to Filter'}
          </Button>
        </Box>
      )}

      <NewTransactionDialog
        open={isNewTransactionDialogOpen}
        onClose={() => setIsNewTransactionDialogOpen(false)}
        onSubmit={handleCreateTransaction}
      />

      <EditTransactionDialog
        open={isEditTransactionDialogOpen}
        onClose={() => {
          setIsEditTransactionDialogOpen(false);
          setSelectedTransaction(null);
        }}
        onSubmit={handleEditTransaction}
        transaction={selectedTransaction}
      />

      <Dialog open={isDeleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this transaction? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};

export default TransactionList; 